import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import '@/App.css';
import Marketplace from './pages/Marketplace';
import PropertySearch from './pages/PropertySearch';
import Dashboard from './pages/Dashboard';
import NewListing from './pages/NewListing';
import NewCampaign from './pages/NewCampaign';
import InfluencerPortal from './pages/InfluencerPortal';
import SubmitBid from './pages/SubmitBid';
import BondCalculator from './pages/BondCalculator';
import Subscription from './pages/Subscription';
import Auth from './pages/Auth';
import AgentProfile from './pages/AgentProfile';
import OwnerProfile from './pages/OwnerProfile';
import HomeServices from './pages/HomeServices';
import ContractorProfile from './pages/ContractorProfile';
import ContractorRegistration from './pages/ContractorRegistration';
import ContractorDashboard from './pages/ContractorDashboard';
import Header from './components/Header';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers from './pages/admin/AdminUsers';
import AdminListings from './pages/admin/AdminListings';
import AdminPayments from './pages/admin/AdminPayments';
import AdminSettings from './pages/admin/AdminSettings';
import AdminReports from './pages/admin/AdminReports';
import AdminContractors from './pages/admin/AdminContractors';
// Static Pages
import AboutPage from './pages/static/AboutPage';
import ContactPage from './pages/static/ContactPage';
import FAQPage from './pages/static/FAQPage';
import PrivacyPolicyPage from './pages/static/PrivacyPolicyPage';
import TermsPage from './pages/static/TermsPage';
import CookiePolicyPage from './pages/static/CookiePolicyPage';
import PAIAManualPage from './pages/static/PAIAManualPage';
import ResetPassword from './pages/ResetPassword';
import SuburbLandingPage from './pages/SuburbLandingPage';
import ListingDetailPage from './pages/ListingDetailPage';
import AuthCallback from './pages/AuthCallback';
import { Toaster } from 'sonner';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getStorage } from 'firebase/storage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Firebase Configuration (Mock values - replace with actual config)
const firebaseConfig = {
  apiKey: "mock-api-key",
  authDomain: "mock-app.firebaseapp.com",
  projectId: "mock-project",
  storageBucket: "mock-app.appspot.com",
  messagingSenderId: "123456789",
  appId: "mock-app-id"
};

// Initialize Firebase
let app, auth, storage;
try {
  app = initializeApp(firebaseConfig);
  auth = getAuth(app);
  storage = getStorage(app);
} catch (error) {
  console.log('Firebase initialization skipped (using mock config)');
}

function App() {
  // Initialize auth state synchronously from localStorage so protected routes
  // don't redirect to /auth during the first render on page reload.
  const [currentUserId, setCurrentUserId] = useState(() => localStorage.getItem('user_id') || null);
  const [appMode, setAppMode] = useState(() => localStorage.getItem('app_user_mode') || 'Guest');
  const [currentView, setCurrentView] = useState('marketplace');
  const [isAuthenticated, setIsAuthenticated] = useState(
    () => Boolean(localStorage.getItem('access_token') && localStorage.getItem('user_id'))
  );
  const [userName, setUserName] = useState(() => localStorage.getItem('user_name') || '');

  useEffect(() => {
    // Sync any updates (e.g. after OAuth callback writes to localStorage)
    const token = localStorage.getItem('access_token');
    const userId = localStorage.getItem('user_id');
    const userMode = localStorage.getItem('app_user_mode');
    const name = localStorage.getItem('user_name');

    if (token && userId) {
      setIsAuthenticated(true);
      setCurrentUserId(userId);
      setAppMode(userMode || 'Guest');
      setUserName(name || '');
    }
  }, []);

  const handleLogin = (user) => {
    setIsAuthenticated(true);
    setCurrentUserId(user.id);
    setAppMode(user.userMode);
    setUserName(user.fullName);
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_name');
    localStorage.removeItem('app_user_mode');
    setIsAuthenticated(false);
    setCurrentUserId(null);
    setAppMode('Guest');
    setUserName('');
  };

  const handleModeChange = (newMode) => {
    setAppMode(newMode);
    localStorage.setItem('app_user_mode', newMode);
  };

  return (
    <div className="App min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <BrowserRouter>
        <Header 
          appMode={appMode} 
          onModeChange={handleModeChange}
          currentUserId={currentUserId}
          isAuthenticated={isAuthenticated}
          userName={userName}
          onLogout={handleLogout}
        />
        <Navigation 
          appMode={appMode}
          currentView={currentView}
          onViewChange={setCurrentView}
        />
        
        <Routes>
          {/* Admin Routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/users" element={<AdminUsers />} />
          <Route path="/admin/listings" element={<AdminListings />} />
          <Route path="/admin/payments" element={<AdminPayments />} />
          <Route path="/admin/reports" element={<AdminReports />} />
          <Route path="/admin/settings" element={<AdminSettings />} />
          <Route path="/admin/contractors" element={<AdminContractors />} />
          
          {/* User Routes */}
          <Route path="/auth" element={<Auth onLogin={handleLogin} />} />
          <Route path="/auth/callback" element={<AuthCallback onLogin={handleLogin} />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/" element={<Marketplace currentUserId={currentUserId} appMode={appMode} />} />
          <Route path="/marketplace" element={<Marketplace currentUserId={currentUserId} appMode={appMode} />} />
          <Route path="/properties" element={<PropertySearch />} />
          <Route path="/listing/:listingId" element={<ListingDetailPage />} />
          <Route path="/dashboard" element={
            isAuthenticated ? <Dashboard currentUserId={currentUserId} appMode={appMode} /> : <Navigate to="/auth" />
          } />
          <Route path="/subscription" element={
            isAuthenticated ? <Subscription currentUserId={currentUserId} appMode={appMode} /> : <Navigate to="/auth" />
          } />
          <Route path="/agent-profile" element={
            isAuthenticated ? <AgentProfile currentUserId={currentUserId} appMode={appMode} /> : <Navigate to="/auth" />
          } />
          <Route path="/owner-profile" element={
            isAuthenticated ? <OwnerProfile currentUserId={currentUserId} appMode={appMode} /> : <Navigate to="/auth" />
          } />
          <Route path="/new-listing" element={
            isAuthenticated ? <NewListing currentUserId={currentUserId} appMode={appMode} storage={storage} /> : <Navigate to="/auth" />
          } />
          <Route path="/new-campaign" element={
            isAuthenticated ? <NewCampaign currentUserId={currentUserId} appMode={appMode} /> : <Navigate to="/auth" />
          } />
          <Route path="/influencer-portal" element={
            isAuthenticated ? <InfluencerPortal currentUserId={currentUserId} /> : <Navigate to="/auth" />
          } />
          <Route path="/submit-bid" element={
            isAuthenticated ? <SubmitBid currentUserId={currentUserId} /> : <Navigate to="/auth" />
          } />
          <Route path="/bond-calculator" element={<BondCalculator />} />
          
          {/* Home Services / Contractor Routes */}
          <Route path="/home-services" element={<HomeServices />} />
          <Route path="/contractor/register" element={<ContractorRegistration />} />
          <Route path="/contractor/dashboard" element={
            isAuthenticated ? <ContractorDashboard currentUserId={currentUserId} /> : <Navigate to="/auth" />
          } />
          <Route path="/contractor/:contractorId" element={<ContractorProfile />} />
          
          {/* Static Pages */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/paia-manual" element={<PAIAManualPage />} />
          
          {/* SEO Landing Pages */}
          <Route path="/houses-for-sale/:suburb" element={<SuburbLandingPage />} />
          <Route path="/houses-to-rent/:suburb" element={<SuburbLandingPage />} />
          <Route path="/apartments-for-sale/:suburb" element={<SuburbLandingPage />} />
          <Route path="/apartments-to-rent/:suburb" element={<SuburbLandingPage />} />
          <Route path="/townhouses-for-sale/:suburb" element={<SuburbLandingPage />} />
          <Route path="/townhouses-to-rent/:suburb" element={<SuburbLandingPage />} />
          <Route path="/land-for-sale/:suburb" element={<SuburbLandingPage />} />
          <Route path="/farms-for-sale/:suburb" element={<SuburbLandingPage />} />
          <Route path="/commercial-for-sale/:suburb" element={<SuburbLandingPage />} />
          <Route path="/commercial-to-rent/:suburb" element={<SuburbLandingPage />} />
          <Route path="/property-for-sale/:suburb" element={<SuburbLandingPage />} />
          <Route path="/property-to-rent/:suburb" element={<SuburbLandingPage />} />
          
          <Route path="*" element={<Navigate to="/marketplace" />} />
        </Routes>
        <Footer />
        <Toaster position="bottom-right" richColors />
      </BrowserRouter>
    </div>
  );
}

export default App;
export { API, storage };